export interface OrganizationLocale {
  currency: string;
  locale: string;
  country_code: string;
  phone_prefix: string;
}

export type OrganizationLocaleSource = Partial<OrganizationLocale> | null | undefined;

export const DEFAULT_LOCALE: OrganizationLocale = {
  currency: 'CLP',
  locale: 'es-CL',
  country_code: 'CL',
  phone_prefix: '+56',
};

export function resolveOrganizationLocale(source?: OrganizationLocaleSource): OrganizationLocale {
  return {
    currency: source?.currency ?? DEFAULT_LOCALE.currency,
    locale: source?.locale ?? DEFAULT_LOCALE.locale,
    country_code: source?.country_code ?? DEFAULT_LOCALE.country_code,
    phone_prefix: source?.phone_prefix ?? DEFAULT_LOCALE.phone_prefix,
  };
}

export function formatCurrency(amount: number, source?: OrganizationLocaleSource): string {
  const org = resolveOrganizationLocale(source);
  return new Intl.NumberFormat(org.locale, {
    style: 'currency',
    currency: org.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatPhone(phone: string, source?: OrganizationLocaleSource): string {
  const org = resolveOrganizationLocale(source);
  const value = phone.trim();

  if (!value) return '';
  if (value.startsWith('+')) return value;

  return `${org.phone_prefix}${value}`;
}

/**
 * Formats a stored phone number for display.
 * "56933610932" → "+56 9 33610932"
 * Falls back to the raw value if format is unrecognized.
 */
export function formatPhoneDisplay(phone: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  // Chile: 56 + 9 digits  →  +56 X XXXXXXXX
  if (digits.startsWith('56') && digits.length === 11) {
    return `+56 ${digits[2]} ${digits.slice(3)}`;
  }
  // Already has a readable format (contains +)
  if (phone.trim().startsWith('+')) return phone.trim();
  return phone;
}

/**
 * Normalizes a raw phone input to the canonical storage format (digits only,
 * with country code). Designed for bulk imports where numbers may lack the
 * country prefix.
 *
 * Examples (Chile):
 *   "912345678"      → "56912345678"
 *   "+56912345678"   → "56912345678"
 *   "0056912345678"  → "56912345678"
 *   "56912345678"    → "56912345678"
 */
export function normalizePhoneForStorage(
  raw: string,
  source?: OrganizationLocaleSource,
): string {
  const org = resolveOrganizationLocale(source);
  // country code digits only, e.g. "56" from "+56"
  const countryDigits = org.phone_prefix.replace(/\D/g, '');

  let digits = raw.replace(/\D/g, '');

  // Strip international access code prefix (00XX)
  if (digits.startsWith('00')) digits = digits.slice(2);

  // Already has country code
  if (digits.startsWith(countryDigits) && digits.length > countryDigits.length + 6) {
    return digits;
  }

  // Prepend country code
  return `${countryDigits}${digits}`;
}

export function formatDateValue(
  value: string | number | Date,
  source?: OrganizationLocaleSource,
  options?: Intl.DateTimeFormatOptions,
): string {
  const org = resolveOrganizationLocale(source);
  return new Intl.DateTimeFormat(org.locale, options).format(new Date(value));
}

export function formatTimeValue(
  value: string | number | Date,
  source?: OrganizationLocaleSource,
  options?: Intl.DateTimeFormatOptions,
): string {
  const org = resolveOrganizationLocale(source);
  return new Intl.DateTimeFormat(org.locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options,
  }).format(new Date(value));
}

/** Formats a date+time value — alias of formatDateValue with datetime-friendly defaults. */
export function formatDateTimeValue(
  value: string | number | Date,
  source?: OrganizationLocaleSource,
  options?: Intl.DateTimeFormatOptions,
): string {
  const org = resolveOrganizationLocale(source);
  return new Intl.DateTimeFormat(org.locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options,
  }).format(new Date(value));
}

export function getPhonePlaceholder(source?: OrganizationLocaleSource): string {
  const org = resolveOrganizationLocale(source);
  return `${org.phone_prefix} 9 1234 5678`;
}
